import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { importGeoVehicles } from "@/utils/importGeoVehicles";
import { ArrowLeft, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function ImportGeoVehicles() {
  const navigate = useNavigate();
  const [importing, setImporting] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);

  const handleImport = async () => {
    setImporting(true);
    setLogs([]);

    const success = await importGeoVehicles((message) => {
      setLogs(prev => [...prev, message]);
    });

    setImporting(false);
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-6 flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Import Geo Vehicles</h1>
          <p className="text-muted-foreground">
            Import Geo vehicle data into the vehicle catalog
          </p>
        </div>
      </div>

      <div className="grid gap-6 max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle>Geo Vehicle Package</CardTitle>
            <CardDescription>
              Import Geo vehicles (Metro, Prizm, Storm, Tracker) as Domestic vehicles.
              The system will automatically group engines by year/make/model.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
              <div>
                <p className="font-medium">geo_ymme.csv</p>
                <p className="text-sm text-muted-foreground">
                  Expected: ~15 unique vehicles (Metro, Prizm, Storm, Tracker)
                </p>
              </div>
              <Button
                onClick={handleImport}
                disabled={importing}
              >
                <Upload className="mr-2 h-4 w-4" />
                {importing ? 'Importing...' : 'Import Now'}
              </Button>
            </div>

            {logs.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-semibold">Import Log</h3>
                <ScrollArea className="h-[300px] w-full rounded-md border p-4">
                  <div className="space-y-1">
                    {logs.map((log, index) => (
                      <div
                        key={index}
                        className="text-sm font-mono text-foreground/80"
                      >
                        {log}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>About This Import</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p>This import will:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Parse the Geo CSV (one engine per row format)</li>
              <li>Group engines by year/make/model combination</li>
              <li>Join multiple engines with semicolons</li>
              <li>Tag all vehicles as package_type: "Domestic"</li>
              <li>Skip any vehicles that already exist in the catalog</li>
              <li>Insert new vehicles in batches for efficiency</li>
            </ul>
            <p className="mt-4 text-muted-foreground">
              After import, Geo vehicles will appear in year/make/model dropdowns when creating tickets.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
